<template>
        <form>

            <div class="input-group" :class="{ error : containsError('name')}">
                <label for="name">Klas</label>
                <input type="text" name="name" id="name" placeholder="Bijvoorbeeld: AH1c" v-model="name" @keyup.enter.prevent="submit" />
            </div>

            <div class="input-group" :class="{ error : containsError('level')}">
                <label for="level">Niveau</label>
                <select name="level" id="level" v-model="level" :disabled="!isCreating || isCreated">
                    <option disabled :value="null">Kies een niveau</option>
                    <option v-for="level in availableLevels" :value="level.id">Jaar {{level.year}}</option>
                </select>
            </div>

            <div v-transfer-dom="'modal__footer'" class="action-wrapper">
                <a href="#" class="tag button-timid button-timid--warning" @click.prevent="detachGroup" v-if="!isCreating || isCreated">Verwijder van mijn overzicht</a>
                <primary-button  @clicked="submit" :loading="loading" >{{ isCreating ? 'Volgende' : 'Opslaan' }}</primary-button>
            </div>

        </form>
</template>


<script>
import api from 'js/api/resources'
import notify from 'js/components/notifications/notificationsMixin'
import validation from 'js/components/auth/validationMixin'
import { transform } from 'js/vuex/helpers'

import PrimaryButton from 'js/components/elements/PrimaryButton.vue'

export default {
    name: 'GroupSettingsGeneral',
    mixins: [ notify, validation ],
    components: { PrimaryButton },
    props: {
        group: {
            required: false, // no value means creating a new one
        },
        school: {},
        isCreating: {
            type: Boolean,
            default: false
        },
        isCreated: {
            type: Boolean,
            default: false
        },
        isDemo: {
            type: Boolean,
            default: false
        },
    },
    data: function() {
        return {
            new_name: null,
            new_level: null,
            availableLevels: [],
            loading: false,
            errors: [],
            errorFields: [],
        }
    },
    vuex: {
        actions: {
            createGroup: ({dispatch}, school, name, level) => {
                return api.addGroup( school.id, name, level )
                    .then( response => transform(response.body) )
                    .then( result => {
                        dispatch('ADD_TERMS', result.terms || [])
                        dispatch('ADD_PERIODS', result.periods || [])
                        dispatch('ADD_GROUP_TERMS', result.groupterms || [])
                        dispatch('ADD_GROUP_PERIODS', result.groupperiods || [])
                        dispatch('ADD_GROUPS', result.groups || [])
                        dispatch('APPEND_GROUP_TO_SCHOOL', result.groups[0], school.id)
                        return result.groups[0]
                    })

            },
            addGroupToTeacher: ({dispatch}, teacherId, groupId) => {
                return api.addGroupToTeacher(teacherId, groupId)
                    .then( response => {
                        const result = transform(response.body)
                        const groups = result.groups || []
                        dispatch('SET_CURRENT_USER_GROUPS', groups.map( g => g.id) )

                        return groups
                    })
            },
            removeGroupFromTeacher: ({dispatch}, teacherId, groupId) => {
                return api.removeGroupFromTeacher(teacherId, groupId)
                    .then( response => {
                        const result = transform(response.body)
                        const groups = result.groups || []
                        dispatch('SET_CURRENT_USER_GROUPS', groups.map( g => g.id) )

                        return groups
                    })
            },
            updateGroup: ({dispatch}, group, name) => {
                return api.patchGroup( group.id, { name } )
                    .then( response => transform(response.body) )
                    .then( result => {
                        dispatch('ADD_GROUPS', result.groups || [])
                        return result.groups || []
                    })
            },
        }
    },
    computed: {

        name: {
            get: function() {
                if (this.new_name != null) {
                    return this.new_name
                }
                return this.group == null ? null : this.group.name
            },
            set: function(value) {
                this.new_name = value
                this.$emit('changed')
            }
        },
        level: {
            get: function() {
                if (this.new_level != null) {
                    return this.new_level
                }
                return this.group == null ? null : this.group.level
            },
            set: function(value) {
                if ( value != this.level ) {
                    this.new_level = value
                    this.$emit('changed')
                }
            }
        },
    },
    methods: {
        submit: function(e) {
            e.preventDefault()
            this.save().catch( () => {} )
        },
        save: function() {

            if ( !this.validate() ) {
                return Promise.reject()
            }

            if ( this.isDemo ) {
                this.$demoSuccessNotification()
                this.$emit('saved')
                return Promise.resolve()
            }

            this.loading = true

            if ( this.isCreating && !this.isCreated ) {

                const p = this.createGroup(this.school, this.name, this.level)
                    .then( group => {
                        return this.addGroupToTeacher(this.$store.state.users.current.id, group.id)
                            .then( () => this.$emit('saved', group) )
                    })
                    .catch( response => {
                        this.handleErrorResponse(response, 'Klas aanmaken is mislukt')
                        return Promise.reject(response)
                    })

                // finally
                p.catch( () => {} ).then( () => this.loading = false)
                return p

            } else {

                const p = this.updateGroup(this.group, this.name)
                    .then( () => this.$emit('saved') )
                    .then( () => this.$success('De instellingen van de klas zijn aangepast') )
                    .catch( response => {
                        this.handleErrorResponse(response, 'Klas aanpassen is mislukt')
                        return Promise.reject(response)
                    })

                // finally
                p.catch( () => {} ).then( () => this.loading = false)
                return p

            }

        },
        validateFields: function() {
            this.validateGroupName(this.name)
            this.validateLevel(this.level)
        },

        validateGroupName: function(name) {
            if ( name == null || name.length < 1) {
                this.addError('name', 'De klasnaam is niet ingevuld')
                return false
            }

            return true
        },
        validateLevel: function(level) {
            if ( this.availableLevels.map( g => g.id).indexOf( level ) === -1 ) {
                this.addError('level', 'Het niveau is niet correct ingevuld')
                return false
            }

            return true
        },

        detachGroup: function() {

            this.$alert('Weet je zeker dat je klas ' + this.group.name + ' wilt verwijderen van jouw overzicht?', [
                { name: 'Annuleren' },
                { name: 'Verwijderen', danger: true, action: () => {
                    if ( this.isDemo ) {
                        this.$demoSuccessNotification()
                        return
                    }

                    this.removeGroupFromTeacher(this.$store.state.users.current.id, this.group.id)
                        .then( () => {
                            this.$emit('saved')
                            this.$emit('done')
                        })
                }}
            ])

        }

    },

    created: function() {

        const request = this.isCreating ? api.getLevelsForSchool(this.school.id) : api.getLevelsForGroup(this.group.id)

        request
            .then( response => transform(response.body) )
            .then( result => {
                const levels = result.levels || []
                const years = result.years || []
                this.availableLevels = levels.map( l => {
                    l.year = years.find( y => y.id == l.year[0] ).name
                    return l
                })
            })

    }

}
</script>


<style lang="scss">

    @import 'global';

    .action-wrapper {
        display: flex;
        align-items: baseline;

        .primary-button {
            margin-left: auto;
        }
    }

</style>



// WEBPACK FOOTER //
// General.vue?a8c7a67a